import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Flex,
  Text,
  Heading,
  Icon,
  Button,
  ButtonGroup,
  Badge,
  Skeleton,
  IconButton,
  SimpleGrid,
  Collapse,
  useDisclosure,
  useBreakpointValue
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Clock, 
  PencilSimple, 
  FloppyDisk, 
  CaretDown,
  CaretUp,
  Plus
} from '@phosphor-icons/react';

// Format the date in a user-friendly way
const formatDate = (date) => {
  if (!date) return 'Unknown date';
  
  // Handle Firestore Timestamp objects by converting to JS Date
  let jsDate;
  try {
    // Check if it's a Firestore Timestamp (has toDate method)
    if (date && typeof date.toDate === 'function') {
      jsDate = date.toDate();
    }
    // Check if it's already a Date object
    else if (date instanceof Date) {
      jsDate = date;
    }
    // If it's an ISO string or other string format, try to create a new Date from it
    else if (typeof date === 'string') {
      jsDate = new Date(date);
    }
    // If it's some other value, try to convert it
    else if (date) {
      jsDate = new Date(date);
    } else {
      return 'Unknown date';
    }
    
    // Validate the date is valid
    if (!jsDate || isNaN(jsDate.getTime())) {
      console.warn("Invalid date received:", date);
      return 'Invalid date';
    }
    
    const now = new Date();
    const diffTime = Math.abs(now - jsDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      return `${Math.floor(diffDays / 7)} weeks ago`;
    } else {
      // Ensure jsDate is definitely a valid Date object before calling toLocaleDateString
      return jsDate instanceof Date && !isNaN(jsDate.getTime())
        ? jsDate.toLocaleDateString()
        : 'Date unavailable';
    }
  } catch (error) {
    console.error('Error formatting date:', error, date);
    return 'Date error';
  }
};

// Calculate days remaining until expiration
const getDaysRemaining = (expiresAt) => {
  if (!expiresAt) return null;
  
  try {
    // Handle Firestore Timestamp objects by converting to JS Date
    let expiryDate;
    
    // Check if it's a Firestore Timestamp
    if (expiresAt && typeof expiresAt.toDate === 'function') {
      expiryDate = expiresAt.toDate();
    }
    // Check if it's already a Date object
    else if (expiresAt instanceof Date) {
      expiryDate = expiresAt;
    }
    // If it's an ISO string or other string format
    else if (typeof expiresAt === 'string') {
      expiryDate = new Date(expiresAt);
    }
    // If it's some other value, try to convert it
    else if (expiresAt) {
      expiryDate = new Date(expiresAt);
    } else {
      return null;
    }
    
    // Validate the date is valid
    if (!expiryDate || isNaN(expiryDate.getTime())) {
      console.warn("Invalid expiry date received:", expiresAt);
      return null;
    }
    
    const now = new Date();
    const diffTime = expiryDate - now;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  } catch (error) {
    console.error('Error calculating days remaining:', error, expiresAt);
    return null;
  }
};

// Format draft status as a badge
const DraftStatusBadge = ({ expiresAt }) => {
  if (!expiresAt) return null;
  
  const daysRemaining = getDaysRemaining(expiresAt);
  
  if (daysRemaining <= 0) {
    return (
      <Badge colorScheme="red" variant="solid" fontSize="xs">
        Expired
      </Badge>
    );
  } else if (daysRemaining <= 7) {
    return (
      <Badge colorScheme="orange" variant="solid" fontSize="xs">
        {daysRemaining}d left
      </Badge>
    );
  } else {
    return (
      <Badge colorScheme="green" variant="subtle" fontSize="xs">
        {daysRemaining}d left
      </Badge>
    );
  }
};

export default function DraftsSection({ drafts = [], isLoading = false }) {
  const { isOpen, onToggle } = useDisclosure();
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // Ensure drafts is always an array to prevent errors
  const draftsArray = Array.isArray(drafts) ? drafts : [];
  
  // Get all drafts for display (not just 3)
  const sortedDrafts = React.useMemo(() => {
    try {
      return [...draftsArray]
        .sort((a, b) => {
          try {
            // Convert Firestore Timestamps to JS Dates for comparison
            let dateA, dateB;
            
            // Safely convert dateA
            if (a?.updatedAt) {
              if (typeof a.updatedAt.toDate === 'function') {
                dateA = a.updatedAt.toDate();
              } else if (a.updatedAt instanceof Date) {
                dateA = a.updatedAt;
              } else if (typeof a.updatedAt === 'string') {
                dateA = new Date(a.updatedAt);
              } else {
                dateA = new Date(a.updatedAt);
              }
            } else {
              dateA = new Date(0);
            }
            
            // Safely convert dateB
            if (b?.updatedAt) {
              if (typeof b.updatedAt.toDate === 'function') {
                dateB = b.updatedAt.toDate();
              } else if (b.updatedAt instanceof Date) {
                dateB = b.updatedAt;
              } else if (typeof b.updatedAt === 'string') {
                dateB = new Date(b.updatedAt);
              } else {
                dateB = new Date(b.updatedAt);
              }
            } else {
              dateB = new Date(0);
            }
          
            // Ensure both are valid dates
            if (!dateA || isNaN(dateA.getTime())) {
              dateA = new Date(0);
            }
            if (!dateB || isNaN(dateB.getTime())) {
              dateB = new Date(0);
            }
            
            return dateB - dateA;
          } catch (error) {
            console.error('Error sorting drafts by date:', error);
            return 0;
          }
        });
    } catch (error) {
      console.error('Error preparing drafts data:', error);
      return [];
    }
  }, [draftsArray]);
  
  const totalDrafts = draftsArray.length;
  
  // Count expired and expiring drafts
  const expiredDrafts = sortedDrafts.filter(draft => {
    const days = getDaysRemaining(draft.expiresAt);
    return days !== null && days <= 0;
  }).length;
  
  const expiringSoon = sortedDrafts.filter(draft => {
    const days = getDaysRemaining(draft.expiresAt);
    return days !== null && days > 0 && days <= 7;
  }).length;

  return (
    <Box variant="dashboardSection" p={0}>
      <VStack align="stretch" spacing={0}>
        {/* Collapsible Header */}
        <Flex
          p={6}
          cursor="pointer"
          onClick={onToggle}
          _hover={{ bg: 'bg.accent' }}
          transition="background-color 0.2s"
          justify="space-between"
          align="center"
        >
          <HStack spacing={3}>
            <Flex
              w="40px"
              h="40px"
              bg="bg.accent"
              color="brand.primary"
              borderRadius="md"
              justify="center"
              align="center"
            >
              <Icon as={FileText} weight="duotone" fontSize="xl" />
            </Flex>
            <VStack align="start" spacing={1}>
              <Heading size="lg" fontFamily="heading" color="text.default">
                Draft Sites
              </Heading>
              <HStack spacing={2}>
                <Badge variant="subtle" colorScheme="gray">
                  {totalDrafts} drafts
                </Badge>
                {expiredDrafts > 0 && (
                  <Badge variant="subtle" colorScheme="red">
                    {expiredDrafts} expired
                  </Badge>
                )}
                {expiringSoon > 0 && (
                  <Badge variant="subtle" colorScheme="orange">
                    {expiringSoon} expiring soon
                  </Badge>
                )}
                <Text fontSize="sm" color="text.muted">
                  Click to {isOpen ? 'hide' : 'view'} drafts
                </Text>
              </HStack>
            </VStack>
          </HStack>
          
          <HStack spacing={3}>
            <Button
              variant="journal"
              size="md"
              leftIcon={<Plus weight="bold" />}
              onClick={(e) => {
                e.stopPropagation();
                navigate('/create-site');
              }}
            >
              New Site
            </Button>
            <IconButton
              icon={<Icon as={isOpen ? CaretUp : CaretDown} weight="bold" />}
              variant="ghost"
              size="md"
              aria-label={isOpen ? "Hide drafts" : "Show drafts"}
              color="text.muted"
            />
          </HStack>
        </Flex>

        {/* Collapsible Content */}
        <Collapse in={isOpen} animateOpacity>
          <VStack align="stretch" spacing={6} px={6} pb={6}>
            {isLoading ? (
              <VStack spacing={4}>
                {[1, 2, 3].map((i) => (
                  <Skeleton
                    key={i}
                    height="120px"
                    width="100%"
                    borderRadius="md"
                    startColor="bg.surface"
                    endColor="bg.accent"
                  />
                ))}
              </VStack>
            ) : totalDrafts === 0 ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                py={12}
                bg="bg.accent"
                borderRadius="md"
              >
                <Icon as={FileText} fontSize="4xl" color="text.muted" mb={4} />
                <Heading size="md" color="text.default" mb={2}>
                  No draft sites yet
                </Heading>
                <Text color="text.muted" textAlign="center" mb={6}>
                  Save your progress when creating new sites and continue editing later
                </Text>
                <Button
                  variant="journal"
                  size="md"
                  leftIcon={<Plus weight="bold" />}
                  onClick={() => navigate('/create-site')}
                >
                  Create Your First Site
                </Button>
              </Flex>
            ) : (
              <>
                {/* Draft Cards Grid */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {sortedDrafts.filter(draft => draft && draft.id).map(draft => (
                    <Box
                      key={draft.id}
                      p={4}
                      borderWidth="1px"
                      borderRadius="md"
                      position="relative"
                      _hover={{
                        borderColor: "brand.primary",
                        transform: 'translateY(-1px)',
                        boxShadow: 'md'
                      }}
                      transition="all 0.2s"
                      bg="bg.surface"
                      borderColor="border.light"
                    >
                      <VStack align="start" spacing={3}>
                        <HStack spacing={3} w="full" justify="space-between">
                          <VStack align="start" spacing={1} flex={1}>
                            <Heading size="sm" noOfLines={1} color="text.default">
                              {(draft.name || draft.siteData?.name) || 'Untitled Site'}
                            </Heading>
                            <HStack spacing={2}>
                              <Icon as={Clock} fontSize="xs" color="text.muted" />
                              <Text fontSize="xs" color="text.muted">
                                {formatDate(draft.updatedAt)}
                              </Text>
                            </HStack>
                          </VStack>
                          <DraftStatusBadge expiresAt={draft.expiresAt} />
                        </HStack>
                        
                        <Text fontSize="sm" noOfLines={2} color="text.muted">
                          {(draft.description || draft.siteData?.description) || 'No description provided.'}
                        </Text>
                        
                        <ButtonGroup size="sm" w="full" spacing={3}>
                          <Button
                            leftIcon={<PencilSimple weight="bold" />}
                            variant="journal"
                            size="sm"
                            flex={1}
                            onClick={() => navigate(`/edit-draft/${draft.id}`)}
                          >
                            Edit Draft
                          </Button>
                          <Button
                            leftIcon={<FloppyDisk weight="bold" />}
                            variant="ghost"
                            color="text.link"
                            size="sm"
                            flex={1}
                            // Add publish functionality later
                          >
                            Publish
                          </Button>
                        </ButtonGroup>
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>

                {/* View All Link */}
                {totalDrafts > 6 && (
                  <Flex justify="center" mt={4}>
                    <Button
                      as={RouterLink}
                      to="/drafts"
                      variant="journal"
                      size="md"
                    >
                      View All {totalDrafts} Drafts
                    </Button>
                  </Flex>
                )}
              </>
            )}
          </VStack>
        </Collapse>
      </VStack>
    </Box>
  );
}
